import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const PAGE_SIZE = 50;
const CACHE_TTL_MS = 30_000; // re-use server data for 30 s between navigations

// Simple module-level cache shared across all hook instances
const _cache = new Map();

/** Invalida caché tras crear/editar producto (p. ej. cambio is_dispensario). */
export function clearAllProductsCache() {
  _cache.clear();
}

function cacheKey(venueID, dispensarioOnly, excludeDispensario, pageIndex, categoryName, expandForSearch) {
  return `${venueID}|${dispensarioOnly}|${excludeDispensario}|${pageIndex}|${categoryName ?? ''}|${expandForSearch ? 'search' : ''}`;
}

/**
 * @param {string} token
 * @param {string} venueID
 * @param {{ dispensarioOnly?: boolean, excludeDispensario?: boolean, categoryName?: string|null, expandForSearch?: boolean }} [options]
 */
export default function useProducts(token, venueID, options = {}) {
  const { dispensarioOnly = false, excludeDispensario = false, categoryName = null, expandForSearch = false } = options;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Abort controller so stale requests don't stomp fresh ones
  const abortRef = useRef(null);

  const fetchProducts = useCallback(async (pageIndex = 0) => {
    if (!token || !venueID) return;

    const key = cacheKey(venueID, dispensarioOnly, excludeDispensario, pageIndex, categoryName, expandForSearch);
    const cached = _cache.get(key);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      setProducts(cached.data);
      // When filtering by category or search we load up to 500 items — no "load more" needed
      setHasMore(!categoryName && !expandForSearch && cached.data.length === PAGE_SIZE);
      return;
    }

    // Cancel any in-flight request for this hook instance
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setErrorMessage('');
    try {
      // Category filter or active search: fetch up to 500 so name/category/id search is reliable.
      const effectiveLimit = categoryName || expandForSearch ? 500 : PAGE_SIZE;
      const params = {
        skip: categoryName || expandForSearch ? 0 : pageIndex * PAGE_SIZE,
        limit: effectiveLimit,
        ...(dispensarioOnly ? { dispensario_only: true } : {}),
        ...(excludeDispensario ? { exclude_dispensario: true } : {}),
        ...(categoryName ? { category_name: categoryName } : {}),
      };
      const { data } = await axios.get('/v1/get-venue-products', {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          'x-venue-id': venueID,
        },
        signal: abortRef.current.signal,
      });
      const list = Array.isArray(data) ? data : [];
      _cache.set(key, { data: list, ts: Date.now() });
      setProducts(list);
      setHasMore(!categoryName && !expandForSearch && list.length === PAGE_SIZE);
    } catch (err) {
      if (axios.isCancel(err)) return;
      const detail = err.response?.data?.detail || err.message || 'Error fetching products';
      setErrorMessage(detail);
    } finally {
      setLoading(false);
    }
  }, [token, venueID, dispensarioOnly, excludeDispensario, categoryName, expandForSearch]);

  const goToPage = useCallback((newPage) => {
    setPage(newPage);
    fetchProducts(newPage);
  }, [fetchProducts]);

  const deleteProduct = useCallback(async (id) => {
    if (!token) return;
    setErrorMessage('');
    try {
      await axios.delete(`/v1/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Bust cache for current page and refetch
      const key = cacheKey(venueID, dispensarioOnly, excludeDispensario, page, categoryName, expandForSearch);
      _cache.delete(key);
      await fetchProducts(page);
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || 'Failed to delete product');
    }
  }, [token, venueID, dispensarioOnly, excludeDispensario, page, categoryName, expandForSearch, fetchProducts]);

  const refreshProducts = useCallback(() => {
    // Force bypass cache on manual refresh
    const key = cacheKey(venueID, dispensarioOnly, excludeDispensario, page, categoryName, expandForSearch);
    _cache.delete(key);
    fetchProducts(page);
  }, [venueID, dispensarioOnly, excludeDispensario, page, categoryName, expandForSearch, fetchProducts]);

  useEffect(() => {
    setPage(0);
    fetchProducts(0);
  }, [fetchProducts]);

  return {
    products,
    loading,
    errorMessage,
    page,
    hasMore,
    pageSize: PAGE_SIZE,
    goToPage,
    refreshProducts,
    deleteProduct,
  };
}
